import {Container} from'@/ui';
import {motion, useScroll, useTransform} from'framer-motion';
import {useRef} from'react';
import {Quote} from'lucide-react';

export interface PhilosophyProps {
  intro: string;
  titleLine1: string;
  titleLine2: string;
  description: string;
  quote: string;
  tagline: string;
}

export const Philosophy = ({
  intro,
  titleLine1,
  titleLine2,
  description,
  quote,
  tagline,
}: PhilosophyProps): React.ReactElement => {
  const sectionRef = useRef<HTMLElement>(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end","end start"]
  });
  const lineWidth = useTransform(scrollYProgress, [0, 0.5], ["0%","100%"]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      }
    }
  };

  const leftVariants = {
    hidden: { opacity: 0, x: -40 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 1,
        ease: [0.16, 1, 0.3, 1] as const,
      }
    }
  };

  const rightVariants = {
    hidden: { opacity: 0, x: 40 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 1,
        ease: [0.16, 1, 0.3, 1] as const,
      }
    }
  };

  return (
    <section
      id="philosophy"
      ref={sectionRef}
      className="py-20 sm:py-28 lg:py-36 relative overflow-hidden bg-muted/30"
    >
      {/* Gradient orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute top-0 left-0 w-full max-w-container-sm aspect-square opacity-30 blur-3xl"
          style={{
            background:'radial-gradient(circle, hsl(var(--accent) / 0.4) 0%, transparent 70%)',
          }}
        />
        <div
          className="absolute bottom-0 right-0 w-full max-w-container-xs aspect-square opacity-25 blur-3xl"
          style={{
            background:'radial-gradient(circle, hsl(var(--primary) / 0.3) 0%, transparent 70%)',
          }}
        />
      </div>

      <Container size="6xl"className="relative z-10">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin:"-100px"}}
          variants={containerVariants}
        >
          <motion.div
            variants={leftVariants}
            className="text-center mb-12 sm:mb-16 lg:mb-20"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 text-accent-foreground text-body-sm font-medium mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-accent motion-safe:animate-pulse"/>
              {intro}
            </span>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div variants={leftVariants}>
              <h2 className="text-heading-1 sm:text-display-3 lg:text-display-2 font-light text-foreground mb-6 lg:mb-8">
                {titleLine1}
                <br />
                <span className="font-normal bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
                  {titleLine2}
                </span>
              </h2>

              {/* Animated gradient line */}
              <motion.div
                style={{ width: lineWidth }}
                className="h-1 bg-gradient-to-r from-primary via-accent to-secondary mb-6 lg:mb-8 max-w-32 rounded-full"
              />

              <p className="text-body-lg text-muted-foreground leading-relaxed">
                {description}
              </p>
            </motion.div>

            <motion.div
              variants={rightVariants}
              className="relative group"
            >
              {/* Animated gradient border */}
              <div className="absolute -inset-border-1 rounded-3xl bg-gradient-to-br from-primary/50 via-accent/30 to-transparent opacity-0 group-hover:opacity-100 active:opacity-100 transition-opacity duration-500 blur-sm"/>

              {/* Quote card */}
              <div className="relative p-8 sm:p-10 rounded-3xl liquid-glass-thick border-[hsl(var(--liquid-glass-border-medium))] shadow-liquid-glass-lg overflow-hidden transition-all duration-[var(--duration-morph)] ease-[var(--ease-liquid)] group-hover:shadow-liquid-glass-lg group-hover:border-[hsl(var(--liquid-glass-border-specular))] active:border-[hsl(var(--liquid-glass-border-specular))]">
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 opacity-0 group-hover:opacity-100 active:opacity-100 transition-opacity duration-500"/>

                {/* Quote icon */}
                <div className="relative z-10 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-accent p-border-1">
                    <div className="w-full h-full rounded-2xl liquid-glass-thin flex items-center justify-center group-hover:bg-transparent active:bg-transparent transition-all duration-[var(--duration-morph)] ease-[var(--ease-liquid)]">
                      <Quote className="w-5 h-5 text-primary group-hover:text-inverse-foreground active:text-inverse-foreground transition-colors duration-300"/>
                    </div>
                  </div>
                </div>

                <p className="relative z-10 text-body-lg sm:text-heading-4 text-foreground font-light leading-relaxed mb-6">
                  {quote}
                </p>

                <p className="relative z-10 text-body text-muted-foreground italic">
                  {tagline}
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </Container>

      {/* Gradient animation */}
      <style>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient {
          animation: gradient 6s ease infinite;
        }
      `}</style>
    </section>
  );
};
